import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, Archive, RefreshCw, MessageSquare, Mail, Phone, Tag, Eye, Pencil } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminCard from "./AdminCard";
import ListingCategoriesManager from "./ListingCategoriesManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUSES = {
    pending: { label: "Oczekuje", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    published: { label: "Opublikowane", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    rejected: { label: "Odrzucone", cls: "bg-red-100 text-red-700 border-red-300" },
    archived: { label: "Archiwum", cls: "bg-gray-200 text-gray-700 border-gray-300" },
};

const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString("pl-PL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
};

function CommentsModal({ listingId, onClose }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get(`/listings/${listingId}/comments`, { params: { page: 1, page_size: 100 } })
            .then((r) => setItems(r.data?.items || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [listingId]);

    const remove = async (id) => {
        if (!window.confirm("Usunąć komentarz?")) return;
        try {
            await api.delete(`/listing-comments/${id}`);
            toast.success("Komentarz usunięty.");
            load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ogloszenia-comments-modal">
            <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[80vh] overflow-auto p-6 md:p-8">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-serif text-xl text-[#092E22]">Komentarze do ogłoszenia</h3>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" data-testid="ogloszenia-comments-close">
                        <X size={18} />
                    </button>
                </div>
                {loading && <p className="text-gray-500 text-sm">Wczytywanie...</p>}
                {!loading && items.length === 0 && <p className="text-gray-500 text-sm">Brak komentarzy.</p>}
                <div className="space-y-3">
                    {items.map((c) => (
                        <article key={c.id} className="border-l-2 border-[#D4A537]/40 pl-4 py-2" data-testid={`admin-listing-comment-${c.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="font-semibold text-[#092E22] text-sm">{c.author_name}</p>
                                    <p className="text-[11px] text-gray-400">{formatDate(c.created_at)}</p>
                                </div>
                                <button onClick={() => remove(c.id)} className="text-red-500 hover:text-red-700 p-1 rounded-lg hover:bg-red-50" title="Usuń" data-testid={`admin-listing-comment-delete-${c.id}`}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-line mt-1.5 break-words">{c.content}</p>
                        </article>
                    ))}
                </div>
            </div>
        </div>
    );
}

function EditModal({ listing, labels, categories, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: listing.title || "",
        content: listing.content || "",
        category: listing.category || "inne",
        contact_name: listing.contact_name || "",
        contact_email: listing.contact_email || "",
        contact_phone: listing.contact_phone || "",
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/listings/${listing.id}`, form);
            toast.success("Ogłoszenie zaktualizowane.");
            onSaved();
            onClose();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ogloszenia-edit-modal">
            <form onSubmit={submit} className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6 md:p-8 space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="font-serif text-xl text-[#092E22]">Edytuj ogłoszenie</h3>
                    <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" data-testid="ogloszenia-edit-close">
                        <X size={18} />
                    </button>
                </div>
                <div className="space-y-2">
                    <Label>Tytuł</Label>
                    <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ogloszenia-edit-title" />
                </div>
                <div className="space-y-2">
                    <Label>Kategoria</Label>
                    <select
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-input bg-white text-sm"
                        data-testid="ogloszenia-edit-category"
                    >
                        {categories.map((k) => <option key={k} value={k}>{labels[k] || k}</option>)}
                    </select>
                </div>
                <div className="space-y-2">
                    <Label>Treść</Label>
                    <Textarea rows={5} required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="ogloszenia-edit-content" />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label>Imię i nazwisko</Label>
                        <Input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="ogloszenia-edit-contact-name" />
                    </div>
                    <div className="space-y-2">
                        <Label>E-mail</Label>
                        <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="ogloszenia-edit-contact-email" />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                        <Label>Telefon</Label>
                        <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="ogloszenia-edit-contact-phone" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Anuluj</Button>
                    <Button type="submit" disabled={saving} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="ogloszenia-edit-save">
                        {saving ? "Zapisuję..." : "Zapisz zmiany"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

export default function OgloszeniaAdmin({ onUpdate }) {
    const [items, setItems] = useState([]);
    const [labels, setLabels] = useState({});
    const [categories, setCategories] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [commentsFor, setCommentsFor] = useState(null);
    const [editing, setEditing] = useState(null);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [counts, setCounts] = useState({ all: 0, pending: 0, published: 0, rejected: 0, archived: 0 });

    const pageSize = 20;

    const fetchCounts = async () => {
        // Get counts for each status (small query, cheap)
        const statuses = ["pending", "published", "rejected", "archived"];
        const results = await Promise.all(statuses.map((s) =>
            api.get("/listings/admin", { params: { status: s, page: 1, page_size: 1 } }).then((r) => r.data?.total || 0).catch(() => 0)
        ));
        const c = { pending: results[0], published: results[1], rejected: results[2], archived: results[3] };
        c.all = c.pending + c.published + c.rejected + c.archived;
        setCounts(c);
    };

    const load = async (p = 1, f = filter) => {
        setLoading(true);
        try {
            const params = { page: p, page_size: pageSize };
            if (f !== "all") params.status = f;
            const [list, cats] = await Promise.all([
                api.get("/listings/admin", { params }),
                api.get("/listings/categories"),
            ]);
            setItems(list.data?.items || []);
            setTotal(list.data?.total || 0);
            setPages(list.data?.pages || 1);
            setPage(list.data?.page || 1);
            setLabels(cats.data?.labels || {});
            setCategories(cats.data?.categories || []);
            await fetchCounts();
            onUpdate?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(1, filter); /* eslint-disable-next-line */ }, [filter]);

    const filtered = items;

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/listings/${id}/status`, null, { params: { status } });
            toast.success("Status zaktualizowany.");
            await load(page, filter);
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Usunąć trwale to ogłoszenie wraz z komentarzami?")) return;
        try {
            await api.delete(`/listings/${id}`);
            toast.success("Ogłoszenie usunięte.");
            await load(page, filter);
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    return (
        <div className="space-y-6" data-testid="ogloszenia-admin">
            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-1">Kategorie ogłoszeń</h3>
                <p className="text-sm text-gray-500 mb-5">
                    Dodawaj, edytuj i usuwaj kategorie, w których mieszkańcy mogą publikować ogłoszenia.
                </p>
                <ListingCategoriesManager onChanged={() => load(page, filter)} />
            </AdminCard>

            <AdminCard>
                <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                    <div>
                        <h3 className="font-serif text-xl text-[#092E22]">Ogłoszenia mieszkańców</h3>
                        <p className="text-sm text-gray-500">Moderacja zgłoszeń, edycja i usuwanie komentarzy.</p>
                    </div>
                    <Button onClick={() => load(page, filter)} variant="outline" size="sm" className="rounded-full" data-testid="ogloszenia-refresh">
                        <RefreshCw size={14} className="mr-2" /> Odśwież
                    </Button>
                </div>

                <div className="flex flex-wrap gap-2 mb-6" data-testid="ogloszenia-status-filter">
                    {["pending", "published", "rejected", "archived", "all"].map((k) => (
                        <button
                            key={k}
                            onClick={() => setFilter(k)}
                            data-testid={`ogloszenia-filter-${k}`}
                            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                filter === k ? "bg-[#0D4F3C] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        >
                            {k === "all" ? "Wszystkie" : STATUSES[k]?.label || k}
                            <span className="ml-2 opacity-70">({counts[k] || 0})</span>
                        </button>
                    ))}
                </div>

                {loading && <p className="text-gray-500 text-sm">Wczytywanie...</p>}
                {!loading && filtered.length === 0 && (
                    <p className="text-gray-500 text-sm" data-testid="ogloszenia-admin-empty">
                        Brak ogłoszeń w tym widoku.
                    </p>
                )}

                <div className="space-y-4">
                    {filtered.map((it) => (
                        <article
                            key={it.id}
                            data-testid={`ogloszenia-row-${it.id}`}
                            className="border border-gray-100 rounded-2xl p-5 hover:border-[#D4A537]/40 transition-colors"
                        >
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${STATUSES[it.status]?.cls || "bg-gray-100 text-gray-700 border-gray-200"}`}>
                                    {STATUSES[it.status]?.label || it.status}
                                </span>
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                    <Tag size={10} /> {labels[it.category] || it.category}
                                </span>
                                <span className="text-[11px] text-gray-400 ml-auto">{formatDate(it.created_at)} · {it.age_days || 0} dni</span>
                            </div>
                            <h4 className="font-serif text-lg text-[#092E22] leading-snug break-words [overflow-wrap:anywhere]">{it.title}</h4>
                            <p className="text-sm text-gray-600 whitespace-pre-line mt-1.5 break-words [overflow-wrap:anywhere]">{it.content}</p>
                            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-500">
                                <span className="font-medium text-gray-700">{it.contact_name}</span>
                                {it.contact_email && <span className="inline-flex items-center gap-1"><Mail size={12} className="text-[#0D4F3C]" /> {it.contact_email}</span>}
                                {it.contact_phone && <span className="inline-flex items-center gap-1"><Phone size={12} className="text-[#0D4F3C]" /> {it.contact_phone}</span>}
                                <button
                                    onClick={() => setCommentsFor(it.id)}
                                    className="inline-flex items-center gap-1 text-[#0D4F3C] hover:text-[#D4A537]"
                                    data-testid={`ogloszenia-show-comments-${it.id}`}
                                >
                                    <MessageSquare size={12} /> Komentarze ({it.comments_count || 0})
                                </button>
                            </div>

                            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-50">
                                {it.status !== "published" && (
                                    <Button onClick={() => setStatus(it.id, "published")} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`ogloszenia-approve-${it.id}`}>
                                        <Check size={14} className="mr-1.5" /> Opublikuj
                                    </Button>
                                )}
                                {it.status !== "rejected" && it.status !== "archived" && (
                                    <Button onClick={() => setStatus(it.id, "rejected")} size="sm" variant="outline" className="rounded-full" data-testid={`ogloszenia-reject-${it.id}`}>
                                        <X size={14} className="mr-1.5" /> Odrzuć
                                    </Button>
                                )}
                                {it.status === "published" && (
                                    <Button onClick={() => setStatus(it.id, "archived")} size="sm" variant="outline" className="rounded-full" data-testid={`ogloszenia-archive-${it.id}`}>
                                        <Archive size={14} className="mr-1.5" /> Archiwizuj
                                    </Button>
                                )}
                                <Button onClick={() => setEditing(it)} size="sm" variant="outline" className="rounded-full" data-testid={`ogloszenia-edit-${it.id}`}>
                                    <Pencil size={14} className="mr-1.5" /> Edytuj
                                </Button>
                                {it.status === "published" && (
                                    <a
                                        href={`/ogloszenie/${it.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full border border-gray-200 hover:bg-gray-50 text-[#0D4F3C]"
                                        data-testid={`ogloszenia-preview-${it.id}`}
                                    >
                                        <Eye size={14} /> Podgląd
                                    </a>
                                )}
                                <Button onClick={() => remove(it.id)} size="sm" variant="outline" className="rounded-full text-red-600 hover:bg-red-50 border-red-200 ml-auto" data-testid={`ogloszenia-delete-${it.id}`}>
                                    <Trash2 size={14} className="mr-1.5" /> Usuń
                                </Button>
                            </div>
                        </article>
                    ))}
                </div>

                {pages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-8" data-testid="ogloszenia-admin-pagination">
                        <button
                            onClick={() => load(Math.max(1, page - 1), filter)}
                            disabled={page <= 1 || loading}
                            data-testid="ogloszenia-admin-prev"
                            className="px-4 py-2 rounded-full border border-gray-200 text-sm bg-white disabled:opacity-40 hover:bg-[#FAF8F3]"
                        >
                            ‹ Poprzednia
                        </button>
                        <span className="text-sm text-gray-600 px-3" data-testid="ogloszenia-admin-page-label">
                            Strona <strong>{page}</strong> z <strong>{pages}</strong> · {total} łącznie
                        </span>
                        <button
                            onClick={() => load(Math.min(pages, page + 1), filter)}
                            disabled={page >= pages || loading}
                            data-testid="ogloszenia-admin-next"
                            className="px-4 py-2 rounded-full border border-gray-200 text-sm bg-white disabled:opacity-40 hover:bg-[#FAF8F3]"
                        >
                            Następna ›
                        </button>
                    </div>
                )}
            </AdminCard>

            {commentsFor && <CommentsModal listingId={commentsFor} onClose={() => setCommentsFor(null)} />}
            {editing && (
                <EditModal
                    listing={editing}
                    labels={labels}
                    categories={categories}
                    onClose={() => setEditing(null)}
                    onSaved={() => load(page, filter)}
                />
            )}
        </div>
    );
}
